import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCatalog, findPackageById, findPackageByName } from "@/lib/CatalogProvider";
import InquiryFlow from "@/components/inquiry/InquiryFlow";

/**
 * StartProject — Full-page route host for the multi-step inquiry flow.
 *
 * Routes handled
 * --------------
 * - /start                          → Package Journey, Route step (default entry).
 * - /start/package                  → Package Journey, Package step.
 * - /start/package/:packageId       → Package Journey, Package pre-selected, Add-ons step.
 * - /start/custom                   → Custom Journey (Coming Soon).
 * - /start/video                    → Legacy alias: routes into Package Journey.
 */
export default function StartProject({ mode }) {
    const { packageId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { packages, loaded } = useCatalog();

    // Support `?package=<name>` query param for legacy links.
    const queryPackageName = useMemo(() => {
        const p = new URLSearchParams(location.search).get("package");
        return p || null;
    }, [location.search]);

    // Resolve deep-link package. If catalog hasn't loaded yet we pass a hint
    // by id/name so InquiryFlow can seed once the packages are available.
    const initialPackage = useMemo(() => {
        if (packageId) {
            const pkg = loaded ? findPackageById(packages, packageId) : null;
            return pkg || { id: packageId, name: "", kind: "" };
        }
        if (queryPackageName) {
            const pkg = loaded ? findPackageByName(packages, queryPackageName) : null;
            return pkg || { id: "", name: queryPackageName, kind: "" };
        }
        return null;
    }, [packageId, queryPackageName, packages, loaded]);

    // Preserved for defensive rendering (existing inquiries may still carry video kind).
    const initialInquiryType = useMemo(() => "branding", []);

    // Which step to land on depends on the entry route.
    const initialStepId = useMemo(() => {
        if (mode === "custom") return "services";      // Skip Route step for /start/custom
        if (packageId) return "addons";                 // Deep-link jumps past package selector
        if (mode === "package-list") return "package";
        return "route";
    }, [mode, packageId]);

    // If someone accidentally lands on `/start/package/:unknown-id` after
    // catalog resolves and we still can't find the package, gracefully fall
    // back to `/start/package`.
    useEffect(() => {
        if (!packageId || !loaded) return;
        const pkg = findPackageById(packages, packageId);
        if (!pkg) navigate("/start/package", { replace: true });
    }, [packageId, packages, loaded, navigate]);

    return (
        <InquiryFlow
            mode={mode === "custom" ? "custom" : "package"}
            initialStepId={initialStepId}
            initialPackage={initialPackage}
            initialInquiryType={initialInquiryType}
        />
    );
}
